// Routing status is a managed list (list-value "Routing status", the team 2026-10-28): managers add,
// rename and retire values in its column's Manage this list; each value has a CATEGORY, and the
// rules read only the category — never the value's words. The seven values the app started with
// keep their meaning. Until a manager first changes the list it has no records: then it is those
// seven, with their categories here.

import type { RoutingStatus } from "../entities";
import { ROUTING_STATUSES, ROUTING_STATUS_LIST } from "../entities";
import type { ListValueRecord } from "./types";

/**
 * What a status means to the rules: Routed counts as routed (the window, shipping, the Routed tab);
 * On hold and Cancelled are the window's On hold, Cancelled also finishes the line; Requested is
 * "Requested · waiting"; Not routed leaves the window to the dates.
 */
export const STATUS_CATEGORIES = ["Not routed", "Requested", "Routed", "On hold", "Cancelled"] as const;
export type StatusCategory = (typeof STATUS_CATEGORIES)[number];

export const isStatusCategory = (v: unknown): v is StatusCategory =>
  typeof v === "string" && (STATUS_CATEGORIES as readonly string[]).includes(v);

/** The seven values the app started with, each with its meaning (spec §5). */
export const STARTING_STATUS_CATEGORY: Readonly<Record<RoutingStatus, StatusCategory>> = {
  "Not routed": "Not routed",
  "Routing requested": "Requested",
  Routed: "Routed",
  "Pre-routed": "Routed",
  "Need info": "On hold",
  "Need extension": "On hold",
  Cancelled: "Cancelled",
};

/** Written by the app itself (a new line, a pre-route's move): never renamed, retired, merged or deleted. */
export const APP_STATUSES: readonly string[] = ["Not routed", "Pre-routed"];
/** Never picked by hand: only a pre-route's move writes it. A line that has it keeps showing it. */
export const NOT_PICKED_STATUSES: readonly string[] = ["Pre-routed"];

/** What the Routing status picker offers: the list's values, not retired, not the app's own Pre-routed. */
export const pickableStatuses = (records: readonly ListValueRecord[] = []) =>
  routingStatusValues(records).filter((s) => !s.retired && !NOT_PICKED_STATUSES.includes(s.value));

/** One value of the list as the pickers and the rules see it. */
export interface StatusValue {
  value: string;
  category: StatusCategory;
  retired: boolean;
  /** Its record; none while the list is the starting seven. */
  record: ListValueRecord | null;
}

const ofList = (records: readonly ListValueRecord[]) => records.filter((r) => r.list === ROUTING_STATUS_LIST);

/** The list: its records once it has any, else the starting seven. */
export function routingStatusValues(records: readonly ListValueRecord[] = []): StatusValue[] {
  const mine = ofList(records);
  if (!mine.length)
    return ROUTING_STATUSES.map((value) => ({
      value,
      category: STARTING_STATUS_CATEGORY[value],
      retired: false,
      record: null,
    }));
  return mine.map((r) => ({
    value: r.value,
    category: isStatusCategory(r.category)
      ? r.category
      : (STARTING_STATUS_CATEGORY[r.value as RoutingStatus] ?? "Not routed"),
    retired: r.retired === "Yes",
    record: r,
  }));
}

/**
 * A status's category: its record's (retired ones too: old lines keep their meaning), else the
 * starting seven's, else Not routed — a value no list knows routes nothing.
 */
export function statusCategory(value: string, records: readonly ListValueRecord[] = []): StatusCategory {
  const rec = ofList(records).find((r) => r.value === value);
  if (rec && isStatusCategory(rec.category)) return rec.category;
  return STARTING_STATUS_CATEGORY[value as RoutingStatus] ?? "Not routed";
}

/** The records "Load starting values" writes: the seven, each with its category. */
export const startingStatusRecords = () =>
  ROUTING_STATUSES.map((value) => ({ value, category: STARTING_STATUS_CATEGORY[value] }));
